/***
 *
 *   ONBOARDING
 *   Flow to help users set up the app. Accepts multiple views.
 *   On finish/cancel the user will be marked as onboarded when save is true.
 *
 *   DOCS
 *   https://docs.useMapMetrics Atlas.app/MapMetrics Atlas-web/components/onboarding
 *
 *   PROPS
 *   onFinish: url to navigate to when finished (string, optional, default: /dashboard)
 *   save: set onboarded column in user database table to true when completed (boolean, optional)
 *   views: array of child views [{ name: string, description: string, component: component }] (array, required)
 *
 **********/

import { Fragment, useState, useEffect } from "react";
import Axios from "axios";
import {
  CheckList,
  Button,
  Logo,
  useNavigate,
  Event,
  Pagination,
  useTranslation,
  useSearchParams,
} from "components/lib";

export function Onboarding({ views, onFinish, save }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // state
  const [activeChildView, setActiveChildView] = useState(0);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    // hash changed - update the child view index
    setActiveChildView((searchParams.get("page") || 1) - 1);
  }, [searchParams]);

  const view = views[activeChildView];
  const isLastView = activeChildView === views.length - 1;

  async function finish() {
    if (save) await Axios.patch("/api/user", { onboarded: true });

    window.location = onFinish || "/dashboard";
  }

  function next() {
    navigate(`?page=${activeChildView + 2}`);
  }

  return (
    <div className="w-[95%] max-w-4xl mx-auto rounded-xl shadow-2xl md:min-h-96 md:flex md:flex-row backdrop-blur-xl bg-white/5 border border-white/10 overflow-hidden">
      {/* sidebar */}
      <aside className="flex flex-col gap-y-4 p-6 border-r border-white/10 w-full md:w-60 flex-shrink-0">
        <Logo className="ml-0 mt-0 w-28" color="light" />

        <CheckList
          className="mt-auto self-start [&_li]:!text-white [&_span]:text-white"
          items={views.map((view, i) => {
            return {
              name: view.name,
              color: i <= activeChildView ? "green" : "white",
              checked: true,
              callback: () => {
                navigate(`?page=${i + 1}`);
              },
            };
          })}
        />

        <Button
          icon="arrow-right-circle"
          iconCSS="flex-none"
          iconSize={16}
          variant="outline"
          text={t("onboarding.button.skip")}
          className="mt-auto glass-input hover:bg-white/10 border-white/30 text-white hover:text-white transition-all duration-300"
          action={() => {
            Event({ name: "cancelled_onboarding" });
            finish();
          }}
        />
      </aside>

      {/* main */}
      <section className="flex flex-col flex-1 gap-y-6 p-6 min-w-0">
        {activeChildView < views.length ? (
          <Fragment>
            <header className="w-full pb-4 border-b border-dotted border-white/20">
              {view.name && (
                <h2 className="capitalize text-xl font-semibold text-white poppins-semibold">
                  {view.name}
                </h2>
              )}

              {view.description && (
                <span className="text-sm text-white/70 poppins-light">
                  {view.description}
                </span>
              )}
            </header>

            <article className="w-full [&_p]:mb-2 [&_label]:text-white [&_input]:text-white [&_select]:text-white [&_button:hover]:bg-white/10 [&_button]:text-white [&>*]:max-w-full">{view.component}</article>

            <div className="mt-auto flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:justify-between">
              {views.length > 1 && (
                <Pagination
                  total={views.length}
                  limit={1}
                  className="!mx-0 !justify-start [&_a]:text-white [&_a]:border-white/30 [&_span]:text-white [&_a:hover]:bg-white/10 [&_a:hover]:text-white"
                />
              )}
              <Button
                icon={isLastView ? "check-circle" : "arrow-right-circle"}
                iconCSS="flex-none"
                iconSize={16}
                text={isLastView ? t("onboarding.button.finish") : t("onboarding.button.next")}
                className="sm:ml-auto bg-blue-500 hover:bg-blue-600 text-white border-0 transition-all duration-300"
                action={isLastView ? finish : next}
              />
            </div>
          </Fragment>
        ) : (
          <div className="text-white/80">Nothing to show</div>
        )}
      </section>
    </div>
  );
}
